<template>
  <div v-if="items && items.length > 0" :class="['analysis-card-compact', cardClass]">
    <div class="card-header-wrapper">
      <h4 class="card-header-compact">
        {{ title }}
      </h4>
      <i 
        @click="toggleEdit" 
        :class="['edit-icon', isEditing ? 'las la-check' : 'las la-edit']" 
        :title="isEditing ? 'Save' : 'Edit'"
      ></i>
    </div>
    <ul v-if="!isEditing" class="analysis-list-compact">
      <li v-for="(item, index) in localItems" :key="index">{{ item }}</li>
    </ul>
    <div v-else class="editable-list">
      <div v-for="(item, index) in localItems" :key="index" class="editable-item">
        <textarea
          v-model="localItems[index]"
          class="editable-textarea"
          rows="2"
          @blur="onItemChange"
        ></textarea>
        <button @click="removeItem(index)" class="remove-item-btn" title="Remove item">
          <i class="las la-times"></i>
        </button>
      </div>
      <button @click="addItem" class="add-item-btn">
        <i class="las la-plus"></i>
        Add Item
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AnalysisCard',
  props: {
    title: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      default: () => []
    },
    cardClass: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isEditing: false,
      localItems: []
    }
  },
  watch: {
    items: {
      immediate: true,
      handler(newItems) {
        this.localItems = newItems ? [...newItems] : []
      }
    }
  },
  methods: {
    toggleEdit() {
      if (this.isEditing) {
        // Save changes
        this.$emit('update', this.localItems)
      }
      this.isEditing = !this.isEditing
    },
    
    onItemChange() {
      // Emit update when item changes
      this.$emit('update', this.localItems)
    },
    
    addItem() {
      this.localItems.push('')
      this.$nextTick(() => {
        const textareas = this.$el.querySelectorAll('.editable-textarea')
        if (textareas.length > 0) {
          textareas[textareas.length - 1].focus()
        }
      })
    },
    
    removeItem(index) {
      this.localItems.splice(index, 1)
      this.$emit('update', this.localItems)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../index.scss';

.analysis-card-compact {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.875rem 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  &.strengths-card {
    border-left: 3px solid #10b981;
  }
  
  &.weaknesses-card {
    border-left: 3px solid #ef4444;
  }
  
  &.risk-strengths-card {
    border-left: 3px solid #3b82f6;
  }
  
  &.risk-weaknesses-card {
    border-left: 3px solid #f59e0b;
  }
  
  &.trends-card {
    border-left: 3px solid #8b5cf6;
  }
  
  &.ratio-card {
    border-left: 3px solid #06b6d4;
  }
  
  &.recommendation-points-card {
    border-left: 3px solid #f59e0b;
  }
  
  &.conclusion-card {
    border-left: 3px solid #6366f1;
  }
}

.card-header-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.625rem;
}

.card-header-compact {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  font-family: $brand-font-medium;
}

.edit-icon {
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s ease;
  font-size: 1.3rem;
  padding: 0.25rem;
  border-radius: 4px;
  
  &:hover {
    background: #f8f9fa;
    color: $color-primary;
  }
}

.editable-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.editable-item {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.editable-textarea {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 1rem;
  font-family: $brand-font-regular;
  color: #495057;
  resize: vertical;
  min-height: 40px;
  
  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 2px rgba($color-primary, 0.1);
  }
}

.remove-item-btn {
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  color: #dc2626;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    background: #fecaca;
    border-color: #dc2626;
  }
  
  i {
    font-size: 1rem;
  }
}

.add-item-btn {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  color: #0284c7;
  transition: all 0.2s ease;
  font-size: 1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: $brand-font-medium;
  
  &:hover {
    background: #bae6fd;
    border-color: #0284c7;
  }
  
  i {
    font-size: 1rem;
  }
}

.analysis-list-compact {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  li {
    padding-left: 1.25rem;
    color: #495057;
    line-height: 1.5;
    font-size: 1rem;
    font-family: $brand-font-regular;
    position: relative;
    
    &:before {
      content: '▸';
      position: absolute;
      left: 0;
      color: $color-primary;
      font-size: 1rem;
      font-weight: bold;
    }
  }
}
</style>

